import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send, CheckCircle2, Mail, FileText, FileArchive, Link2 } from "lucide-react";

const fmtDate = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

export default function DaInviarePage() {
  const { t } = useTranslation("app");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rapportini-da-inviare");
      setItems(data?.items || []);
      setSelected({});
    } catch {
      toast.error(t("daInviare.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const daInviare = useMemo(() => items.filter((i) => !i.inviato), [items]);
  const inviati = useMemo(() => items.filter((i) => i.inviato), [items]);
  const selIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allSelected = daInviare.length > 0 && selIds.length === daInviare.length;

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(daInviare.map((i) => [i.id, true])));
  };

  const apriAnteprima = async () => {
    if (!selIds.length || previewLoading) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/rapportini-invia-bulk/preview", { ids: selIds });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("daInviare.sentFail"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const inviaSelezionati = async () => {
    if (!selIds.length || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/rapportini-invia-bulk", { ids: selIds });
      setPreview(null);
      if (data?.status === "demo") {
        toast.info(t("daInviare.demoBlock"));
      } else if ((data?.sent || 0) > 0) {
        const key = data.mode === "zip" ? "daInviare.sentZipOk" : "daInviare.sentOk";
        toast.success(t(key, { n: data.sent, dest: (data.recipients || []).join(", "), zip: data.zip_name || "" }), { duration: 8000 });
      } else {
        toast.error(data?.message || t("daInviare.sentFail"));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("daInviare.sentFail"));
    } finally {
      setSending(false);
    }
  };

  const Row = ({ r, selectable }) => (
    <div
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 hover:border-[#0044FF]/30 transition-colors"
      data-testid={`da-inviare-row-${r.numero}`}
    >
      {selectable && (
        <Checkbox
          checked={!!selected[r.id]}
          onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))}
          data-testid={`da-inviare-check-${r.numero}`}
        />
      )}
      <Link to={`/rapportino/${r.id}`} className="flex-1 min-w-0 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-blue-50 grid place-items-center shrink-0">
          <FileText className="h-5 w-5 text-[#0044FF]" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate">
            {t("daInviare.rapNum", { n: r.numero })} · {fmtDate(r.data)}
          </div>
          <div className="text-xs text-slate-500">
            {r.ore_tot} h{r.vidimato ? ` · ${t("daInviare.vidimato")}` : ""}
          </div>
        </div>
      </Link>
      {r.inviato ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 shrink-0" title={(r.inviato_a || []).join(", ")}>
          <CheckCircle2 className="h-3.5 w-3.5" />
          {t("daInviare.inviatoIl", { d: fmtDate(r.inviato_il || "") })}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 shrink-0">
          <Mail className="h-3.5 w-3.5" />
          {t("daInviare.nonInviato")}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="da-inviare-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">{t("daInviare.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("daInviare.subtitle")}</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> {t("daInviare.loading")}
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-base text-slate-900">
                {t("daInviare.sectionDaInviare")} ({daInviare.length})
              </h2>
              {daInviare.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={toggleAll} data-testid="da-inviare-select-all">
                    {allSelected ? t("daInviare.deselectAll") : t("daInviare.selectAll")}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!selIds.length || sending || previewLoading}
                    onClick={apriAnteprima}
                    className="bg-[#0044FF] hover:bg-[#0033CC]"
                    data-testid="da-inviare-send-btn"
                  >
                    {sending || previewLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                    {t("daInviare.sendBtn", { n: selIds.length })}
                  </Button>
                </div>
              )}
            </div>
            {daInviare.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 text-center" data-testid="da-inviare-empty">
                {t("daInviare.emptyDaInviare")}
              </div>
            ) : (
              <div className="space-y-2">{daInviare.map((r) => <Row key={r.id} r={r} selectable />)}</div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="font-heading font-bold text-base text-slate-900">
              {t("daInviare.sectionInviati")} ({inviati.length})
            </h2>
            {inviati.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 text-center">
                {t("daInviare.emptyInviati")}
              </div>
            ) : (
              <div className="space-y-2">{inviati.map((r) => <Row key={r.id} r={r} />)}</div>
            )}
          </section>
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o && !sending) setPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="da-inviare-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {preview?.mode === "zip" ? t("daInviare.previewTitleZip", { n: preview?.files?.length || 0 }) : t("daInviare.previewTitleSingle")}
            </DialogTitle>
            <DialogDescription>
              {t("daInviare.previewTo")} <b className="text-slate-800">{(preview?.recipients || []).join(", ") || "—"}</b>
              {preview?.subject && <><br />{t("daInviare.previewSubject")} <span className="text-slate-800">{preview.subject}</span></>}
            </DialogDescription>
          </DialogHeader>
          {preview?.link && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs sm:text-sm text-amber-900" data-testid="da-inviare-preview-link-info">
              <Link2 className="h-4 w-4 shrink-0 mt-0.5" /> {t("daInviare.previewLinkInfo", { h: preview.link_hours || 24 })}
            </div>
          )}
          {preview?.zip_name && (
            <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-sm font-semibold text-[#0044FF]" data-testid="da-inviare-preview-zip">
              <FileArchive className="h-4 w-4 shrink-0" /> {preview.zip_name}
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="da-inviare-preview-files">
            {(preview?.files || []).map((f) => (
              <li key={f.id} className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm" data-testid={`da-inviare-preview-file-${f.numero}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="font-mono text-slate-800 break-all">{f.filename}</span>
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreview(null)} disabled={sending} data-testid="da-inviare-preview-cancel">
              {t("daInviare.previewCancel")}
            </Button>
            <Button onClick={inviaSelezionati} disabled={sending} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="da-inviare-preview-confirm">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              {t("daInviare.previewConfirm", { n: preview?.files?.length || 0 })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
